import { ticketEntriesApi } from "../api";
import { buildCsv, csvFilename, downloadCsvFile } from "./csvExport";
import type { TicketEntryLog } from "../types";

const EXPORT_PAGE_SIZE = 200;
const EXPORT_MAX_PAGES = 50;

function formatOccurredAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export function ticketEntriesToCsv(entries: TicketEntryLog[]): string {
  const headers = [
    "Occurred At",
    "Tier",
    "Quantity Sold",
    "Operator Name",
    "Operator Email",
    "Ticketing Partner",
    "Amount (INR)",
    "Notes",
    "Entry ID",
  ];

  const rows = entries.map((entry) => [
    formatOccurredAt(entry.occurred_at),
    entry.tier_name?.trim() || "",
    String(entry.quantity_sold ?? 1),
    entry.operator_name?.trim() || "",
    entry.operator_email?.trim() || "",
    entry.ticketing_partner_name?.trim() || "",
    String(Number.isFinite(entry.entry_cost) ? entry.entry_cost : 0),
    entry.notes?.trim() || "",
    entry.id,
  ]);

  return buildCsv(headers, rows);
}

export function downloadTicketEntriesCsv(entries: TicketEntryLog[], filename: string): void {
  downloadCsvFile(ticketEntriesToCsv(entries), filename);
}

export function ticketEntryExportFilename(eventId: string): string {
  return csvFilename("ticket-entries", eventId);
}

export async function fetchAllTicketEntries(eventId: string, token: string): Promise<TicketEntryLog[]> {
  const byId = new Map<string, TicketEntryLog>();
  let cursor: string | undefined;

  for (let page = 0; page < EXPORT_MAX_PAGES; page += 1) {
    const response = await ticketEntriesApi.list(
      eventId,
      { direction: "desc", limit: EXPORT_PAGE_SIZE, cursor },
      token,
    );

    response.items.forEach((entry) => byId.set(entry.id, entry));

    if (!response.next_cursor || response.items.length === 0) {
      break;
    }
    cursor = response.next_cursor;
  }

  return Array.from(byId.values()).sort(
    (a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime(),
  );
}
